import { FormattedMessage } from 'react-intl';
import { AlertCode } from 'shared/types/checkout';
import { AlertTypes, PaymentFAQsLink, SupportLink } from 'components/ui/atoms';

export const getAlertType = (key: AlertCode) =>
  ({
    redeem_discount: AlertTypes.INFO,
    remove_discount: AlertTypes.INFO,
  })[key as string] ?? AlertTypes.DANGER;

export const getAlertMessage = (key: AlertCode, value?: string) =>
  ({
    cart_changed: (
      <FormattedMessage
        id="payment.checkout.error.cart.changed"
        defaultMessage="Your cart has changed since navigating to this page. Please reload the page and verify the product you are purchasing."
        description="Notifies the user their cart has changed, so they can reload the page to see what it actually contains."
      />
    ),
    cart_load_failed: (
      <FormattedMessage
        id="payment.checkout.error.cart.load.failed"
        defaultMessage="There was an unexpected problem while loading your cart. If the problem persists, please {supportLink}."
        description="The error message when we fail to load the cart for an unknown reason"
        values={{
          supportLink: <SupportLink />,
        }}
      />
    ),
    fallback: (
      <FormattedMessage
        id="payment.checkout.error.fallback"
        defaultMessage="There was an unexpected problem. If the problem persists, please {supportLink}."
        description="The error message when we don't know the specific error"
        values={{
          supportLink: <SupportLink />,
        }}
      />
    ),
    fallback_with_reload_information: (
      <FormattedMessage
        id="payment.checkout.error.reload.fallback"
        defaultMessage="There was an unexpected problem. Please reload the page. If the problem persists, please {supportLink}."
        description="The error message when we don't know the specific error"
        values={{
          supportLink: <SupportLink />,
        }}
      />
    ),
    coupon_not_eligible_for_cart: (
      <FormattedMessage
        id="payment.checkout.coupon.not.eligible.for.cart"
        defaultMessage="Cart does not qualify for coupon code &lsquo;{discountCode}&lsquo;"
        description="Message to display when a discount code does not matches the cart"
        values={{ discountCode: value }}
      />
    ),
    coupon_not_valid_for_cart: (
      <FormattedMessage
        id="payment.checkout.coupon.not.valid.for.cart"
        defaultMessage="Coupon code &lsquo;{discountCode}&rsquo; is not valid for this cart."
        description="Message to display when a discount code is not valid for the cart"
        values={{ discountCode: value }}
      />
    ),
    coupon_not_eligible_for_bundled_purchase: (
      <FormattedMessage
        id="payment.checkout.coupon.not.eligible.for.bundled.purchase"
        defaultMessage="Coupon code &lsquo;{discountCode}&rsquo; is not valid for this cart for a bundled purchase."
        description="Message to display when a discount code does not matches the cart for a bundled purchase"
        values={{ discountCode: value }}
      />
    ),

    invalid_coupon: (
      <FormattedMessage
        id="payment.checkout.invalid.coupon.already.used"
        defaultMessage="Coupon code &lsquo;{discountCode}&rsquo; is not available. You have already used this coupon in a previous order."
        description="Message to display when a discount code is invalid for the cart"
        values={{ discountCode: value }}
      />
    ),
    discount_code_expired: (
      <FormattedMessage
        id="payment.checkout.discount.code.expired"
        defaultMessage="Coupon code &lsquo;{discountCode}&lsquo; has expired."
        description="Message to display when a discount code is expired"
        values={{ discountCode: value }}
      />
    ),
    payment_processing_failed: (
      <FormattedMessage
        id="payment.checkout.error.payment.processing.failed"
        defaultMessage="There was an unexpected problem while processing your payment. If the problem persists, please {supportLink}."
        description="The error message when we fail to process the payment for an unknown reason"
        values={{
          supportLink: <SupportLink />,
        }}
      />
    ),
    order_processing_failed: (
      <FormattedMessage
        id="payment.checkout.error.payment.processing.failed"
        defaultMessage="There was an unexpected problem while processing your order. If the problem persists, please {supportLink}."
        description="The error message when we fail to process the payment for an unknown reason"
        values={{
          supportLink: <SupportLink />,
        }}
      />
    ),
    product_not_found: (
      <FormattedMessage
        id="payment.checkout.error.product.not.found"
        defaultMessage="Products with SKU(s) [{sku}] do not exist."
        description="Notifies the user the product they are trying to purchase is no longer available."
        values={{ sku: value }}
      />
    ),
    product_entitlements_enrollment_fetch_error: null,
    product_credit_eligibility_error: (
      <FormattedMessage
        id="payment.checkout.error.product.unavailable"
        defaultMessage="An error has occurred. We could not confirm that you are eligible for course credit.
                               Try the transaction again."
        description="Notifies the user the eligibility for course credit could not be confirmed."
      />
    ),
    product_unavailable: (
      <FormattedMessage
        id="payment.checkout.error.product.unavailable"
        defaultMessage="The product you are trying to purchase is no longer available. If you need assistance, please {supportLink}."
        description="Notifies the user the product they are trying to purchase is no longer available."
        values={{
          supportLink: <SupportLink />,
        }}
      />
    ),
    redeem_discount: (
      <FormattedMessage
        id="payment.checkout.add.coupon.message"
        defaultMessage="Coupon code &lsquo;{discountCode}&rsquo; added to cart."
        description="Message to display when a discount code is applied to the cart"
        values={{ discountCode: value }}
      />
    ),
    remove_discount: (
      <FormattedMessage
        id="payment.checkout.remove.coupon.message"
        defaultMessage="Coupon code &lsquo;{discountCode}&rsquo; was removed from your cart."
        description="Message to display when a discount code is removed from the cart"
        values={{ discountCode: value }}
      />
    ),
    sku_not_provided: (
      <FormattedMessage
        id="payment.checkout.error.sku.not.provided"
        defaultMessage="SKU not provided. If you need assistance, please {supportLink}."
        description="The error message when the SKU is not provided"
        values={{
          supportLink: <SupportLink />,
        }}
      />
    ),
    transaction_declined: (
      <FormattedMessage
        id="payment.checkout.error.transaction.declined"
        defaultMessage="Your payment could not be processed. Please check your payment information or reach out to your bank or financial institution for further assistance."
        description="Asks the user to check their information or contact their payment provider for help."
      />
    ),
    product_unavailable_to_buy: (
      <FormattedMessage
        id="payment.checkout.error.product.unavailable.to.buy"
        defaultMessage="No product is available to buy."
        description="Notifies the user the product they are trying to purchase is no longer available to buy."
      />
    ),
    order_already_placed: (
      <FormattedMessage
        id="payment.checkout.error.order.already.placed"
        defaultMessage="You have already purchased these products. If you need assistance, please {supportLink}."
        description="Notifies the user the product they are trying to purchase is already bought."
        values={{
          supportLink: <SupportLink />,
        }}
      />
    ),
    paypal_payment_processing_failed: (
      <FormattedMessage
        id="payment.checkout.error.paypal.payment.processing.failed"
        defaultMessage="An error has occurred with your payment. You have not been charged. Please try to submit your payment again. If this problem persists, please refer to our {paymentFAQsLink} for troubleshooting tips."
        description="The error message when we fail to process the payment for an unknown reason and ask the user to refer to the payment FAQs"
        values={{
          paymentFAQsLink: <PaymentFAQsLink />,
        }}
      />
    ),
    paypal_transaction_cancelled: (
      <FormattedMessage
        id="payment.checkout.error.paypal.transaction.cancelled"
        defaultMessage="Your transaction has been canceled. If you believe this was an error, please {supportLink}"
        description="The error message when the user cancels a transaction"
        values={{
          supportLink: <SupportLink />,
        }}
      />
    ),
    discount_code_not_provided: (
      <FormattedMessage
        id="redeem.discount.code.not.provided"
        defaultMessage="Discount code not provided."
        description="Message to display when a discount code is not provided"
      />
    ),
    discount_not_found: (
      <FormattedMessage
        id="redeem.discount.code.not.found"
        defaultMessage="The discount code you entered could not be found. Please check the code and try again."
        description="Message to display when the discount code is not found"
      />
    ),
  })[key];
